import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import Card, { SectionTitle } from "@/components/base/Card";
import NotificationPreferencesCard from "./components/NotificationPreferencesCard";
import Button from "@/components/base/Button";
import { Input } from "@/components/base/Field";
import PhotoUploader from "@/components/base/PhotoUploader";
import CycleBadge from "@/pages/dashboard/users/components/CycleBadge";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { STRONG_PASSWORD, STRONG_PASSWORD_HINT } from "@/services/authService";

export default function AccountPage() {
  const { notify } = useToast();
  const { user, role, changePassword, updateProfile } = useAuth();
  const [savingProfile, setSavingProfile] = useState(false);
  const navigate = useNavigate();
  const [savingPwd, setSavingPwd] = useState(false);

  const [profile, setProfile] = useState({
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    phone: user?.phone ?? "",
  });
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [avatar, setAvatar] = useState(user?.avatar ?? "");

  const displayName = user ? `${user.firstName} ${user.lastName}` : "Utilisateur";

  const persistAvatar = async (value: string) => {
    setAvatar(value);
    try {
      await updateProfile({ avatar: value });
      notify(value ? "Photo de profil mise à jour." : "Photo de profil retirée.");
    } catch (err) {
      notify(err instanceof Error ? err.message : "Enregistrement de la photo impossible.", "error");
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader title="Mon compte" subtitle="Vos informations personnelles, votre mot de passe et vos alertes" icon="ri-user-settings-line" />

      <div className="max-w-3xl space-y-5">
        <Card>
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
            <PhotoUploader
              value={avatar}
              onChange={persistAvatar}
              lastName={user?.lastName ?? ""}
              firstName={user?.firstName ?? ""}
              label=""
              hint="PNG, JPG ou WEBP · réduite automatiquement"
              size="lg"
            />
            <div className="min-w-0 flex-1">
              <p className="text-base font-semibold font-heading text-foreground-950">{displayName}</p>
              <p className="text-sm text-foreground-500">{user?.email ?? "—"}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                  <i className="ri-shield-user-line"></i>
                  {role?.name ?? user?.roleName ?? "Aucun rôle"}
                </span>
                <CycleBadge scope={role?.cycleScope ?? user?.cycleScope ?? "GLOBAL"} />
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <SectionTitle icon="ri-user-line" title="Informations personnelles" subtitle="Mettez à jour vos données de profil" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Prénom" value={profile.firstName} onChange={(e) => setProfile({ ...profile, firstName: e.target.value })} />
            <Input label="Nom" value={profile.lastName} onChange={(e) => setProfile({ ...profile, lastName: e.target.value })} />
            <Input label="Email" type="email" value={profile.email} disabled hint="Identifiant de connexion : modifiable par un administrateur." />
            <Input label="Téléphone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
          </div>
          <div className="mt-5 flex justify-end">
            <Button
              icon="ri-save-3-line"
              disabled={savingProfile}
              onClick={async () => {
                if (!profile.firstName.trim() || !profile.lastName.trim()) {
                  notify("Le prénom et le nom sont obligatoires.", "error");
                  return;
                }
                setSavingProfile(true);
                try {
                  await updateProfile({
                    firstName: profile.firstName,
                    lastName: profile.lastName,
                    phone: profile.phone,
                  });
                  notify("Informations personnelles enregistrées.");
                } catch (err) {
                  notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
                } finally {
                  setSavingProfile(false);
                }
              }}
            >
              Enregistrer
            </Button>
          </div>
        </Card>

        <Card>
          <SectionTitle icon="ri-lock-password-line" title="Mot de passe" subtitle="Choisissez un mot de passe solide et unique" />
          <div className="grid grid-cols-1 gap-4 sm:max-w-md">
            <Input label="Mot de passe actuel" type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} />
            <Input label="Nouveau mot de passe" type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} hint={STRONG_PASSWORD_HINT} />
            <Input label="Confirmer le nouveau mot de passe" type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} />
          </div>
          <div className="mt-5 flex justify-end">
            <Button
              icon="ri-shield-check-line"
              disabled={savingPwd}
              onClick={async () => {
                if (!pwd.current || !pwd.next) {
                  notify("Remplissez les champs du mot de passe.", "error");
                  return;
                }
                if (!STRONG_PASSWORD.test(pwd.next)) {
                  notify(`Nouveau mot de passe trop faible : ${STRONG_PASSWORD_HINT}.`, "error");
                  return;
                }
                if (pwd.next !== pwd.confirm) {
                  notify("La confirmation ne correspond pas au nouveau mot de passe.", "error");
                  return;
                }
                setSavingPwd(true);
                try {
                  // Le serveur ferme la session : reconnexion avec le nouveau mot de passe.
                  await changePassword(pwd.current, pwd.next);
                  navigate("/login", {
                    replace: true,
                    state: { notice: "Mot de passe changé. Connectez-vous avec votre nouveau mot de passe." },
                  });
                } catch (err) {
                  notify(err instanceof Error ? err.message : "Changement impossible.", "error");
                  setSavingPwd(false);
                }
              }}
            >
              Mettre à jour
            </Button>
          </div>
        </Card>

        <NotificationPreferencesCard />
      </div>
    </div>
  );
}